import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/contexts/AuthContext";
import { PRODUCT_FORM } from "@/constants/testIds";
import { ChevronRight, Eye, Save, FileText } from "lucide-react";
import BasicInfoTab from "@/components/tabs/BasicInfoTab";
import MediaTab from "@/components/tabs/MediaTab";
import ContentTab from "@/components/tabs/ContentTab";
import VariantsTab from "@/components/tabs/VariantsTab";
import MOQTab from "@/components/tabs/MOQTab";
import SuggestiveSPTab from "@/components/tabs/SuggestiveSPTab";
import RelatedProductsTab from "@/components/tabs/RelatedProductsTab";
import SpecsQATab from "@/components/tabs/SpecsQATab";

const TABS = [
  { id: "basic", label: "Basic", fullLabel: "Basic Information" },
  { id: "media", label: "Media", fullLabel: "Media" },
  { id: "content", label: "Content", fullLabel: "Content" },
  { id: "variants", label: "Variants", fullLabel: "Variants" },
  { id: "moq", label: "MOQ", fullLabel: "MOQ" },
  { id: "suggestive", label: "Suggestive SP", fullLabel: "Suggestive SP" },
  { id: "related", label: "Related Products", fullLabel: "Related Products" },
  { id: "specs", label: "Specifications & Q&A", fullLabel: "Specifications & Q&A" },
];

const INITIAL_DATA = {
  product_id: "", item_name: "", product_type: "", collection: [], brand_name: "",
  item_highlight: "", product_sku: "", status: "Draft", url_slug: "",
  search_keywords: [], hsn_code: "", tax_rate: 18, country_of_origin: "India",
  manufacturer: "", package_includes: "", delivery_timeline: "",
  shipping_weight: 0, product_dimension: "", material: "", color: "",
  electrical: "Non-Electrical", return_policy: "Non-returnable",
  mrp: 0, deal_price_excl_gst: 0, deal_price_incl_gst: 0,
  star_rating: 0, num_reviews: 0, offers: "",
  main_image: "", gallery_images: [], video_url: "", media_description: "",
  product_description: "", product_features: [],
  variant_options: [], variant_combinations: [],
  moq_tiers: [{ min_qty: 1, price: 0 }],
  suggestive_sp: {
    amazon: { item_price: 0, referral_fee_pct: 5, fulfilment_cost: 45, tax_on_fees_pct: 0, other_cost: 0, product_cost: 0 },
    flipkart: { item_price: 0, referral_fee_pct: 5, fulfilment_cost: 45, tax_on_fees_pct: 0, other_cost: 0, product_cost: 0 },
    meesho: { item_price: 0, referral_fee_pct: 12, fulfilment_cost: 30, tax_on_fees_pct: 0, other_cost: 0, product_cost: 0 },
    myntra: { item_price: 0, referral_fee_pct: 25, fulfilment_cost: 50, tax_on_fees_pct: 0, other_cost: 0, product_cost: 0 },
    shopify: { item_price: 0, referral_fee_pct: 2, fulfilment_cost: 20, tax_on_fees_pct: 0, other_cost: 0, product_cost: 0 },
  },
  related_product_ids: [],
  shipping_returns: {
    deliver_within: "2-5", ship_within: "1-2",
    shipping_weight: "250", shipping_dimension: "21 x 29.7 x 1",
    return_policy: "Non-returnable"
  },
  specifications: [], qa_items: [],
};

export default function AddEditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("basic");
  const [data, setData] = useState(INITIAL_DATA);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const isEdit = !!id;

  useEffect(() => {
    if (id) {
      axios.get(`${API}/products/${id}`)
        .then(res => {
          const p = res.data;
          setData({ ...INITIAL_DATA, ...p });
        })
        .catch(() => {
          toast.error("Failed to load product");
          navigate("/admin/products");
        })
        .finally(() => setLoading(false));
    }
  }, [id]);

  const update = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (statusOverride) => {
    const payload = statusOverride ? { ...data, status: statusOverride } : data;
    if (!payload.item_name.trim()) {
      toast.error("Product name is required");
      setActiveTab("basic");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/products/${id}`, payload);
        toast.success("Product updated successfully!");
      } else {
        const res = await axios.post(`${API}/products`, payload);
        toast.success("Product created successfully!");
        navigate(`/admin/products/${res.data.id}`);
      }
    } catch (err) {
      toast.error("Failed to save product");
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (id) {
      // Use MongoDB ID so preview works for both Draft and Live products
      window.open(`/products/${id}`, "_blank");
    } else if (data.url_slug) {
      toast.info("Save the product first for a full preview");
      window.open(`/products/${data.url_slug}`, "_blank");
    } else {
      toast.info("Save the product first to preview");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2 text-sm">
          <Link to="/admin/products" className="text-slate-500 hover:text-orange-500 transition-colors">
            Products
          </Link>
          <ChevronRight className="w-4 h-4 text-slate-400" />
          <span className="text-slate-900 font-medium">
            {isEdit ? "Edit Product" : "Add New Product"}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            data-testid={PRODUCT_FORM.previewButton}
            onClick={handlePreview}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 border border-slate-300 hover:border-slate-400 px-3 py-1.5 rounded-lg transition-all"
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
          <button
            data-testid={PRODUCT_FORM.saveDraftButton}
            onClick={() => handleSave("Draft")}
            disabled={saving}
            className="flex items-center gap-1.5 text-sm text-slate-700 border border-slate-300 hover:bg-slate-50 px-3 py-1.5 rounded-lg transition-all disabled:opacity-60"
          >
            <FileText className="w-3.5 h-3.5" />
            Save as Draft
          </button>
          <button
            data-testid={PRODUCT_FORM.saveProductButton}
            onClick={() => handleSave()}
            disabled={saving}
            className="flex items-center gap-1.5 text-sm bg-orange-500 hover:bg-orange-600 text-white px-4 py-1.5 rounded-lg transition-all shadow-sm disabled:opacity-60"
          >
            {saving ? (
              <span className="flex items-center gap-1.5">
                <div className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <>
                <Save className="w-3.5 h-3.5" />
                Save Product
              </>
            )}
          </button>
        </div>
      </div>

      {/* Add New Product title */}
      <div className="px-6 pt-4 pb-0">
        <h1 className="text-lg font-bold text-slate-900">
          {isEdit ? "Edit Product" : "Add New Product"}
        </h1>
      </div>

      {/* Tab Navigation */}
      <div className="mx-6 mt-3 bg-white rounded-t-xl border border-slate-200 border-b-0">
        <div className="flex overflow-x-auto tab-scroll border-b border-slate-200">
          {TABS.map((tab, idx) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}-trigger`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${
                activeTab === tab.id
                  ? "border-orange-500 text-orange-600 bg-orange-50/50"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:bg-slate-50"
              }`}
            >
              <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold flex-shrink-0 ${
                activeTab === tab.id ? "bg-orange-500 text-white" : "bg-slate-200 text-slate-600"
              }`}>
                {idx + 1}
              </span>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="mx-6 bg-white rounded-b-xl border border-slate-200 border-t-0 p-6 mb-6">
        {activeTab === "basic" && <BasicInfoTab data={data} onChange={update} />}
        {activeTab === "media" && <MediaTab data={data} onChange={update} />}
        {activeTab === "content" && <ContentTab data={data} onChange={update} />}
        {activeTab === "variants" && <VariantsTab data={data} onChange={update} />}
        {activeTab === "moq" && <MOQTab data={data} onChange={update} />}
        {activeTab === "suggestive" && <SuggestiveSPTab data={data} onChange={update} />}
        {activeTab === "related" && <RelatedProductsTab data={data} onChange={update} />}
        {activeTab === "specs" && <SpecsQATab data={data} onChange={update} />}
      </div>
    </div>
  );
}
